import React, { useState } from "react";
import { Card, Alert, Row, Col, Affix } from "antd";
import LoadingIndicator from "../../components/shared/LoadingIndicator/LoadingIndicator";
import ErrorBoundary from "../../components/shared/ErrorBoundary/ErrorBoundary";
import RefreshContentAnalysisButton from "./RefreshContentAnalysisButton";
import ContentTrust from "./ContentTrust";
import DomainBusinessContext from "./DomainBusinessContext";
import ContentEffectivenessReport from "./ContentEffectivenessReport";
import CompetitorContentAnalysis from "./CompetitorContentAnalysis";
import useDomainContentAnalysis from "./hooks/useDomainContentAnalysis";
import "./ContentAnalysis.less";


const { Meta } = Card;
type Props = {
    domain: string;
}

export default function ContentAnalysis({ domain }: Props) {
    const [, setIsRefreshingAnalysis] = useState(false);
    const { data: domainContentAnalysis, isLoading, isInProgress, activeProcess } = useDomainContentAnalysis(domain);

    const domainBusinessContextInProgress = isInProgress && !activeProcess.websiteProfile;
    const contentEffectivenessInProgress = isInProgress && !activeProcess.contentEffectiveness;
    const competitorsContentInProgress = isInProgress && !activeProcess.competitorContent;
    const contentTrustInProgress = isInProgress && !activeProcess.contentTrust;

    return (
        <Card
            className="domain-content-analysis"
            title={
                <Meta title="AI Content Analysis" description="What LLMs knows about your domain's content" />
            }
            extra={
                !isLoading ? <RefreshContentAnalysisButton domain={domain} inProgress={isInProgress} setIsRefreshingAnalysis={setIsRefreshingAnalysis} /> : null
            }
        >
            {isLoading && <LoadingIndicator />}

            {!isLoading && isInProgress && (
                <Alert
                    className="analysis-in-progress-alert"
                    type="info"
                    showIcon
                    message={
                        <div className="analysis-progress">
                            <div className="title">AI Content Analysis in progress</div>
                            <div className="loader"></div>
                        </div>
                    }
                    description={
                        <>
                            <span className="alert-message">
                                We are performing an AI Content Analysis for your domain. This may take a couple of minutes, depending on your domain's content complexity.&nbsp;
                            </span>
                            <span className="alert-message">
                                Feel free to leave this page - your analysis will continue running in the background.
                            </span>
                        </>
                    }
                />
            )}

            {/*
              * Boundary sits inside the Card so the refresh button in `extra` survives a render
              * error here. Re-running the analysis may or may not produce a result that renders,
              * but without the button the user has no way to try.
              */}
            {!isLoading && (
                <ErrorBoundary
                    fallback={
                        <Alert
                            className="analysis-in-progress-alert"
                            type="warning"
                            showIcon
                            message="This analysis could not be displayed"
                            description="Something went wrong while rendering this analysis."
                        />
                    }
                >
                    <Row gutter={24}>
                        <Col span={18} className="domain-content-analysis-col">
                            <DomainBusinessContext
                                domainDescription={domainContentAnalysis?.profile.domain_description || activeProcess?.websiteProfile?.domain_description}
                                targetAudience={domainContentAnalysis?.profile.target_audience || activeProcess?.websiteProfile?.target_audience}
                                industryNiche={domainContentAnalysis?.profile.industry_niche.description || activeProcess?.websiteProfile?.industry_niche.description}
                                inProgress={domainBusinessContextInProgress}
                            />

                            <ContentEffectivenessReport
                                industryNiche={domainContentAnalysis?.profile.industry_niche.name || activeProcess?.websiteProfile?.industry_niche.name}
                                summary={domainContentAnalysis?.content_effectiveness.summary || activeProcess?.contentEffectiveness?.summary}
                                analysis={domainContentAnalysis?.content_effectiveness.analysis || activeProcess?.contentEffectiveness?.analysis}
                                contentOpportunities={domainContentAnalysis?.content_effectiveness.content_opportunities || activeProcess?.contentEffectiveness?.content_opportunities}
                                inProgress={contentEffectivenessInProgress}
                            />

                            <CompetitorContentAnalysis
                                summary={domainContentAnalysis?.competitor_content?.summary || activeProcess?.competitorContent?.summary}
                                sections={domainContentAnalysis?.competitor_content?.sections || activeProcess.competitorContent?.sections}
                                inProgress={competitorsContentInProgress}
                            />
                        </Col>

                        <Col span={6} className="content-trust-col">
                            <Affix offsetTop={70} className="sticky">
                                <ContentTrust
                                    summary={domainContentAnalysis?.content_trust?.summary || activeProcess?.contentTrust?.summary}
                                    improvements={domainContentAnalysis?.content_trust?.improvements || activeProcess?.contentTrust?.improvements}
                                    trustMetrics={domainContentAnalysis?.content_trust?.trust_metrics || activeProcess?.contentTrust?.trust_metrics}
                                    trustScore={domainContentAnalysis?.content_trust?.trust_score || activeProcess?.contentTrust?.trust_score}
                                    inProgress={contentTrustInProgress}
                                />
                            </Affix>
                        </Col>
                    </Row>
                </ErrorBoundary>
            )}
        </Card >
    );
}